import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '@forwardflow/store/appStore'
import { IStage, useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup } from '@forwardflow/types/onboarding'
import { initialStageMappingsGroups } from '@forwardflow/constants/onboarding.constants'
import {
   useGetFubStages,
   useGetPeopleTags,
   useGetSuggestedStageMappings,
} from '@forwardflow/queries/onboarding.queries'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import {
   DEMO_STAGES,
   DEMO_SUGGESTIONS,
   DEMO_TAGS,
   isFreeFlow,
   useDevTools,
} from '../dev/devTools'
import {
   applyAllSuggestions,
   hasAnySelection,
   StageMappingExplainedView,
} from './stage-mapping-explained'

/**
 * Last onboarding step: stage mapping, explained.
 *
 * Reads the same Follow Up Boss stages, tags and suggested mappings the
 * drag-and-drop screen used, stores the result in the same `stageMappings`
 * shape, and completes onboarding (which used to happen on the phone step).
 */
export const StageMappingExplained = () => {
   const navigate = useNavigate()
   const { currentOrganization } = useAppStore()
   const {
      onboardingData,
      getPreviousStep,
      setStageMappings,
      setIsOnBoardingCompleted,
   } = useOnboardingStoreV2()

   const orgId = onboardingData.organizationId || currentOrganization || ''

   const { data: fubStages, isLoading: isLoadingStages } = useGetFubStages(
      orgId,
      10000,
      1,
      true
   )
   const { data: fubTags, isLoading: isLoadingTags } = useGetPeopleTags(
      orgId,
      1,
      10000,
      true
   )

   const stages: IStage[] = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({
               id: String(s.stageId),
               name: s.stageName,
               description: '',
               type: 'stage' as const,
            })) || [],
      [fubStages]
   )
   const tags: IStage[] = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .map((t) => ({
               id: t.tagName,
               name: t.tagName,
               description: '',
               type: 'tag' as const,
            })) || [],
      [fubTags]
   )

   const stagesForSuggestion = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({ stageId: s.stageId, stageName: s.stageName })) || [],
      [fubStages]
   )
   const tagsForSuggestion = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .map((t, index) => ({ tagId: index + 1, tagName: t.tagName })) || [],
      [fubTags]
   )

   const { data: suggested, isLoading: isLoadingSuggestions } =
      useGetSuggestedStageMappings(
         stagesForSuggestion,
         tagsForSuggestion,
         !isLoadingStages && !isLoadingTags
      )

   const groups: IStageMapGroup[] = onboardingData.stageMappings?.length
      ? onboardingData.stageMappings
      : initialStageMappingsGroups

   // Free flow (dev tools): demo stages, tags and suggestions when Follow Up
   // Boss returned nothing, and no required-bucket gate on Finish.
   const freeFlow = isFreeFlow()
   const useDemo =
      freeFlow && !isLoadingStages && !isLoadingTags && stages.length === 0
   const stagesShown = useDemo ? DEMO_STAGES : stages
   const tagsShown = useDemo ? DEMO_TAGS : tags
   const suggestionsShown = useDemo ? DEMO_SUGGESTIONS : suggested?.data

   // Pre-fill Forward Flow's suggestions once, only on a mapping nobody has
   // touched yet. A returning customer keeps what they saved.
   const prefilled = useRef(false)
   useEffect(() => {
      if (prefilled.current || !suggestionsShown?.length) return
      prefilled.current = true
      if (hasAnySelection(groups)) return
      setStageMappings(applyAllSuggestions(groups, suggestionsShown))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [suggestionsShown])

   const finish = () => {
      setStageMappings(groups)
      if (!isOperatorEmail(onboardingData.email || '')) {
         setIsOnBoardingCompleted(true)
      }
      navigate('/dashboard')
   }

   useDevTools({
      fill: () => {
         if (suggestionsShown) {
            setStageMappings(applyAllSuggestions(groups, suggestionsShown))
         }
      },
   })

   return (
      <StageMappingExplainedView
         groups={groups}
         stages={stagesShown}
         tags={tagsShown}
         suggestions={suggestionsShown}
         isLoadingOptions={isLoadingStages || isLoadingTags}
         isLoadingSuggestions={
            !useDemo &&
            (isLoadingSuggestions ||
               (!suggested &&
                  !isLoadingStages &&
                  !isLoadingTags &&
                  (stagesForSuggestion.length > 0 ||
                     tagsForSuggestion.length > 0)))
         }
         requireAll={!freeFlow}
         isCompleted={onboardingData.isOnBoardingCompleted}
         onChange={setStageMappings}
         onBack={() => getPreviousStep()}
         onFinish={finish}
      />
   )
}
